import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { connect, useSelector } from "react-redux";
import moment from "moment";
import lodashGet from "lodash/get";

// import Card from "@material-ui/core/Card";
import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import withStyles from "@material-ui/styles/withStyles";
import Card from "@material-ui/core/Card";

import {
  PageHeader,
  FilterButton,
  Filter,
  ReferenceArrayInput,
  SelectArrayInput,
  AutoCompleteArrayInput,
  ReferenceInput,
  SelectInput,
  DateInput
} from "jazasoft";

import MacroController from "./MacroController";

import CardHeader from "../../../components/CardHeader";
import Forbidden from "../../../components/Forbidden";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { useDeepMemo, useQuery } from "../../../hooks";
import { isEmpty } from "../../../utils/helpers";
import Table from "jazasoft/lib/mui/components/Table";
import { UOM } from "../../../constants";

const UomLabelFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.label }), {});

const inputOptions = {
  xs: 12,
  fullWidth: true,
  options: { fullWidth: true }
};

const dateKey = "orderCloseDate";

const uomChoices = [
  { id: UOM.METER.value, name: UOM.METER.label },
  { id: UOM.KILOGRAM.value, name: UOM.KILOGRAM.label }
];

const defaultFilterKeys = ["start", "end", "fabricUom", "unit", "season", "product", "fabricPattern", "fabricCategory", "supplier", "customer"];

const styles = theme => ({
  root: {},
  actions: {
    zIndex: 2,
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap"
  },
  content: {
    margin: theme.spacing(3),
    marginTop: 0
  },
  header: {},
  noResults: { padding: 20 }
});

export const ReportView = props => {
  const {
    roles,
    hasAccess,
    filterKeys = defaultFilterKeys,
    children,
    className,
    classes = {},
    title,
    i18nKey,
    resource,
    data,
    products,
    fabricPatterns,
    fabricCategories,
    suppliers,
    seasons,
    customers,
    orderList,
    isLoading,
    setFilter,
    fetchSettings,
    filterValues,
    filterDefaultValues,
    toggleFilter,
    filterOpen,
    translate,
    version,
    history,
    dispatch,
    totalLabel
  } = props;

  const { data: unitChoicesFromSummary = [] } = useQuery({ url: "report-summary/units" });
  const unitChoices = unitChoicesFromSummary?.map(e => ({ id: e, name: e }));

  const [total, setTotal] = React.useState(0);

  const i18nMessages = useSelector(state => state?.i18n?.messages) || {};

  const filter = filterValues
    ? {
        ...filterDefaultValues[dateKey],
        ...filterValues,
        unit: !isEmpty(filterValues?.unitId) ? unitChoices.find(e => e.id === filterValues.unitId)?.name : "",
        customerList: filterValues.customerIds && filterValues.customerIds.map(id => customers[id]).map(({ name }) => ({ name })),
        season: filterValues.seasonId && seasons[filterValues.seasonId] && seasons[filterValues.seasonId].name,
        supplierList: filterValues.supplierIds && filterValues.supplierIds.map(id => suppliers[id]).map(({ name }) => ({ name })),
        productList: filterValues.productIds && filterValues.productIds.map(id => products[id]).map(({ name }) => ({ name })),
        fabricPatternList: filterValues.fabricPatternIds && filterValues.fabricPatternIds.map(id => fabricPatterns[id]).map(({ name }) => ({ name })),
        fabricCategoryList:
          filterValues.fabricCategoryIds && filterValues.fabricCategoryIds.map(id => fabricCategories[id]).map(({ name }) => ({ name })),
        total
      }
    : filterDefaultValues[dateKey];

  const rows = [
    {
      ...filter,
      fabricUom: isEmpty(filter?.fabricUom) ? filter?.fabricUom : UomLabelFromValue[filter.fabricUom],
      start: moment(filter.start).format("ll"),
      end: moment(filter.end).format("ll"),
      total
    }
  ];

  const columns = useDeepMemo(() => {
    const cols = filterKeys
      .map(dataKey => ({ dataKey, title: lodashGet(i18nMessages || {}, `resources.report.filter.${dataKey}`, dataKey) }))
      .filter(c => !isEmpty(rows[0][c.dataKey]));
    if (!isEmpty(totalLabel)) {
      cols.push({ dataKey: "total", title: totalLabel });
    }
    return cols;
  }, [filterKeys, rows, i18nMessages, totalLabel]);

  const orders = orderList.filter(o => {
    let result = true;
    if (filter.start && filter.end) {
      result = o.orderCloseDate >= moment(filter.start).startOf("day").valueOf() && o.orderCloseDate <= moment(filter.end).endOf("day").valueOf();
    }
    if (result && filter.customerList && filter.customerList.length > 0) {
      result = filter.customerList.map(({ name }) => name).includes(o.customer);
    }
    if (result && filter.season) {
      result = filter.season === o.season;
    }
    if (result && filter.line) {
      result = filter.line === o.line;
    }
    if (result && filter.productList && filter.productList.length > 0) {
      result = filter.productList.map(({ name }) => name).includes(o.product);
    }
    if (result && filter.supplierList && filter.supplierList.length > 0) {
      result = filter.supplierList.map(({ name }) => name).includes(o.supplier);
    }
    if (result && filter.fabricPatternList && filter.fabricPatternList.length > 0) {
      result = filter.fabricPatternList.map(({ name }) => name).includes(o.fabricPattern);
    }
    if (result && filter.fabricCategoryList && filter.fabricCategoryList.length > 0) {
      result = filter.fabricCategoryList.map(({ name }) => name).includes(o.fabricCategory);
    }
    return result;
  });

  const controllerProps = {
    i18nKey,
    resource,
    data,
    orderList: orders,
    isLoading,
    setFilter,
    fetchSettings,
    toggleFilter,
    filterOpen,
    translate,
    version,
    dispatch,
    filter,
    updateTotal: setTotal
  };

  if (!hasPrivilege(roles, hasAccess, ResourceId.MACRO, Scope.READ)) {
    return <Forbidden history={history} />;
  }

  return (
    <div className={classnames(classes.root, className)}>
      <PageHeader title={title}>
        <div>
          <FilterButton showLabel={false} resource={resource} />
        </div>
      </PageHeader>

      {filterKeys.length > 0 && (
        <Drawer anchor="right" open={filterOpen} onClose={() => toggleFilter(false)}>
          <Filter
            parse={({
              start,
              end,
              fabricUom,
              unitId,
              unitIds,
              line,
              customerIds,
              seasonId,
              productIds,
              supplierIds,
              fabricPatternIds,
              fabricCategoryIds
            }) => {
              return {
                fabricUom,
                unitId,
                unitIds: !isEmpty(unitIds) ? `*${unitIds}*` : undefined,
                line,
                customer: customerIds && customerIds.map(id => customers[id] && customers[id].name),
                season: seasonId && seasons[seasonId] && seasons[seasonId].name,
                product: productIds && productIds.map(id => products[id] && products[id].name),
                supplier: supplierIds && supplierIds.map(id => suppliers[id] && suppliers[id].name),
                fabricPattern: fabricPatternIds && fabricPatternIds.map(id => fabricPatterns[id] && fabricPatterns[id].name),
                fabricCategory: fabricCategoryIds && fabricCategoryIds.map(id => fabricCategories[id] && fabricCategories[id].name),
                [dateKey]: {
                  start: moment(start).startOf("day").valueOf(),
                  end: moment(end).endOf("day").valueOf()
                }
              };
            }}
            initialValues={filterValues || filterDefaultValues[dateKey]}
            setFilter={setFilter}
            translate={translate}
            version={version}>
            {filterKeys.includes("start") && <DateInput label="Start Date" source="start" {...inputOptions} />}
            {filterKeys.includes("end") && <DateInput label="End Date" source="end" {...inputOptions} />}
            {filterKeys.includes("fabricUom") && <SelectInput label="UOM" source="fabricUom" choices={uomChoices} {...inputOptions} />}
            {filterKeys.includes("unit") && <SelectInput label="Unit" source="unitId" choices={unitChoices} {...inputOptions} />}
            {filterKeys.includes("unitIds") && <SelectInput label="Unit" source="unitIds" choices={unitChoices} {...inputOptions} />}
            {filterKeys.includes("product") && (
              <ReferenceArrayInput
                label="Product"
                resource="products"
                source="productIds"
                reference="products"
                pageSize={2000}
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <SelectArrayInput />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("season") && (
              <ReferenceInput
                label="Season"
                resource="seasons"
                source="seasonId"
                reference="seasons"
                pageSize={100}
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}
                allowEmpty={true}>
                <SelectInput />
              </ReferenceInput>
            )}
            {filterKeys.includes("fabricPattern") && (
              <ReferenceArrayInput
                label="Fabric Type"
                resource="fabric-patterns"
                source="fabricPatternIds"
                reference="fabric-patterns"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <SelectArrayInput />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("fabricCategory") && (
              <ReferenceArrayInput
                label="Fabric Category"
                resource="fabric-categories"
                source="fabricCategoryIds"
                reference="fabric-categories"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <SelectArrayInput />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("supplier") && (
              <ReferenceArrayInput
                label="Mill"
                resource="suppliers"
                source="supplierIds"
                reference="suppliers"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <AutoCompleteArrayInput disablePortal={true} />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("customer") && (
              <ReferenceArrayInput
                label="Customer"
                resource="customers"
                source="customerIds"
                reference="customers"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <AutoCompleteArrayInput disablePortal={true} />
              </ReferenceArrayInput>
            )}
          </Filter>
        </Drawer>
      )}

      <div key={version} className={classes.content}>
        <Card>
          <CardHeader title="Filters" />
          <Divider />
          <Table columns={columns} rows={rows} />
        </Card>

        {children &&
          React.cloneElement(children, {
            ...controllerProps
          })}
      </div>
    </div>
  );
};

ReportView.propTypes = {
  basePath: PropTypes.string,
  children: PropTypes.element,
  className: PropTypes.string,
  classes: PropTypes.object,
  title: PropTypes.any,
  defaultTitle: PropTypes.string,
  filterDefaultValues: PropTypes.object,
  filters: PropTypes.element,
  filterValues: PropTypes.object,
  hideFilter: PropTypes.func,
  isLoading: PropTypes.bool,
  refresh: PropTypes.func,
  resource: PropTypes.string,
  i18nKey: PropTypes.string,
  setFilter: PropTypes.func,
  showFilter: PropTypes.func,
  translate: PropTypes.func,
  version: PropTypes.number,
  updateTotal: PropTypes.func
};

const Report = props => <MacroController {...props}>{controllerProps => <ReportView {...props} {...controllerProps} />}</MacroController>;

Report.propTypes = {
  // the props you can change
  children: PropTypes.node,
  classes: PropTypes.object,
  className: PropTypes.string,
  filter: PropTypes.object,
  filterDefaultValues: PropTypes.object,
  filters: PropTypes.element,
  title: PropTypes.any,

  // the props managed by react-admin
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  path: PropTypes.string,
  resource: PropTypes.string,
  i18nKey: PropTypes.string
};

Report.defaultProps = {
  filter: {},
  pageSize: 8
};

export default withStyles(styles)(connect()(Report));
